import { CinematicProject } from './CinematicProject';
import { motion } from 'framer-motion';
import { ScrollReveal } from './ScrollReveal';
import { ScrapbookSticker } from './scrapbook/ScrapbookSticker';
import gumihoImage from '../images/gumiho-cover.png';
import audioDescriptionImage from '../images/audio-description-demo.png';
import computationalFabricsImage from '../images/computational-fabrics-cover.jpg';
// ClipIt image
const clipitImage = '/images/ci2.portfolio.png';

export function ProjectsSection() {
  const projects = [
    {
      title: "ClipIt — Language Learning Through Video",
      layoutPattern: 'design-product',
      question: "Can the media you already watch become part of learning a language?",
      description: "A language-learning tool that turns vocabulary from Netflix and YouTube into personalized spaced-repetition flashcards.",
      image: clipitImage,
      tags: ["React", "TypeScript", "Chrome Extension", "Spaced Repetition"],
      keyInsights: [
        {
          type: 'statistic',
          primary: "~20 students",
          secondary: "piloting ClipIt in a Dartmouth Korean course"
        },
        {
          type: 'finding',
          primary: "Watch → Capture → Review",
          secondary: "Learning starts with media students already enjoy"
        }
      ],
      videoLink: "https://drive.google.com/file/d/1229t8D9MofTdx-JMJfdBRQCSDTnh7BEB/view?usp=sharing",
      isIndependent: false,
      isGroupProject: true
    },

    {
      title: "Computational Fabrics for Athletes",
      layoutPattern: 'research-driven',
      question: "What if performance tracking disappeared into the fabric you already wear?",
      description: "Mixed-methods UX research with 14 collegiate athletes investigating computational fabrics for performance monitoring.",
      image: computationalFabricsImage,
      tags: ["UX Research", "User Interviews", "Survey Design", "Thematic Analysis"],
      features: [
        "Conducted semi-structured interviews with 5 active wearable technology users",
        "Performed LDA analysis of r/WearableTechnology to identify key themes",
        "Surveyed 14 collegiate athletes across rugby, football, and lacrosse",
        "Identified critical gap between technical feasibility and user-centered implementation"
      ],
      keyInsights: [
        {
          type: 'statistic',
          primary: "78.5%",
          secondary: "already track performance metrics"
        },
        {
          type: 'finding',
          primary: "Tactile > Visual",
          secondary: "Athletes preferred feedback that didn't require visual attention during activity"
        }
      ],
      paperLink: "/papers/computational-fabrics-paper.pdf",
      researchHighlights: [
        "78.5% of athletes actively track performance metrics despite device limitations",
        "Distance and speed most tracked across all contact sports (13 and 10 mentions)",
        "Athletes prefer tactile/auditory feedback over visual displays during activity",
        "89% adoption rate for team-provided tech vs 33% for individual purchase"
      ],
      isResearch: true,
    },

    {
      title: "Audio Description for Accessibility",
      layoutPattern: 'pivot-narrative',
      question: "When should technology automate, and when should it empower user choice?",
      description: "Investigated automated audio description for video accessibility using Voice Activity Detection to analyze silence gaps.",
      image: audioDescriptionImage,
      tags: ["Accessibility Research", "Python", "WebRTC VAD", "YAMNet"],
      features: [
        "Developed Voice Activity Detection pipeline for gap analysis",
        "Implemented audio event filtering to avoid narrating over meaningful sounds",
        "Analyzed feasibility across multiple video genres",
        "Pivoted to on-demand model based on empirical findings"
      ],
      narrative: {
        problem: "Automated descriptions could be inserted into detected gaps",
        insight: "Automatic insertion isn't feasible across most video genres.",
        pivot: "Prioritize user-controlled, on-demand descriptions"
      },
      researchHighlights: [
        "Automatic audio description insertion not feasible across most video genres",
        "Music videos and action sequences have insufficient silence gaps for narration",
        "User-controlled, on-demand approach preserves user agency and flexibility",
        "Technical solution shifted from automation to user empowerment based on empirical data"
      ],
      paperLink: "/papers/audio-description-paper.pdf",
      demoVideoLink: "https://drive.google.com/file/d/1TJk0fsBD-v-QiqD38ma8U6LfGTwVbtzY/view?usp=sharing",
      isResearch: true
    },

    {
      title: "Gumiho",
      layoutPattern: 'minimal-visual',
      question: "Can language learning feel like play instead of study?",
      description: "Narrative game teaching Korean through comprehensible input, branching dialogue, and interactive storytelling.",
      image: gumihoImage,
      tags: ["Unity", "C#", "Dialogue Systems", "Quest Mechanics"],
      features: [
        "Language acquisition through comprehensible input methodology",
        "Complex branching dialogue system",
        "Cultural storytelling rooted in Korean folklore",
        "Quest-based progression that reinforces learning"
      ],
      demoLink: "https://julianmatos.itch.io/gumiho",
      isIndependent: true
    },
  ];

  return (
    <section id="projects" className="relative overflow-hidden">
      {/* Smooth horizon transition - no hard boundary */}
      <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-transparent via-slate-950/30 to-transparent z-0 pointer-events-none" />

      {/* Shallow water atmosphere - just below surface */}
      <div className="absolute top-0 left-0 right-0 h-[60vh] pointer-events-none overflow-hidden z-[1]">
        {/* Light rays from above - very subtle */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute top-0 w-[1px] h-full opacity-[0.03]"
            style={{
              left: `${15 + i * 15}%`,
              background: 'linear-gradient(to bottom, rgba(200,220,240,0.3) 0%, transparent 50%)',
              transform: `rotate(${-3 + i}deg)`,
              transformOrigin: 'top',
            }}
          />
        ))}

        {/* Underwater haze */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/10 to-transparent opacity-40" />

        {/* Very sparse suspended particles */}
        {[...Array(4)].map((_, i) => {
          const left = 20 + i * 20;
          const top = 15 + i * 15;
          return (
            <div
              key={i}
              className="absolute w-[2px] h-[2px] rounded-full bg-slate-300/20"
              style={{
                left: `${left}%`,
                top: `${top}%`,
              }}
            />
          );
        })}
      </div>

      {/* Section intro */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 pb-16">
        <ScrollReveal>
          <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-6">
            <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
            <span>Selected Work</span>
          </div>
        </ScrollReveal>

        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
          className="text-editorial-question font-bold text-white max-w-5xl"
          style={{
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
          }}
        >
          Exploring better ways to learn, interact, and create
        </motion.h2>
      </div>

      {/* Projects flow continuously */}
      <div className="relative z-10">
        {projects.map((project, index) => (
          <CinematicProject key={index} project={project} index={index} />
        ))}

        {/* Scrapbook stickers - very sparse, in negative space */}

        {/* Sticker 1 - shallow water, right edge near ClipIt/Computational Fabrics transition */}
        <div className="absolute top-[30%] right-[3%] hidden lg:block pointer-events-none">
          <ScrapbookSticker
            src="/images/scrap2.jpg"
            size={70}
            rotation={-4}
            depth="shallow"
            opacity={0.8}
          />
        </div>

        {/* Sticker 2 - deeper water, partially outside viewport near Audio Description */}
        <div className="absolute top-[72%] right-[12%] hidden lg:block pointer-events-none">
          <ScrapbookSticker
            src="/images/scrap1.png"
            size={140}
            rotation={3}
            depth="deep"
            opacity={0.65}
            removeBackground={true}
          />
        </div>
      </div>
    </section>
  );
}